import { Chess, Move, Square } from "chess.js";

interface PuzzleMoveHandlerParams {
    move: Move;
    gameState: {
        game: Chess | null;
        puzzleIndex?: number;
        puzzleSolution?: string[];
    };
    setGameState: React.Dispatch<React.SetStateAction<any>>;
    setIncorrectMoveSquare: (square: Square) => void;
    setShowIncorrectMoveModal: (show: boolean) => void;
    setShowPuzzleCompleteModal: (show: boolean) => void;
    resetSelection: () => void;
    puzzleStateBeforeMove: React.MutableRefObject<{ fen: string; index: number } | null>;
}

/**
 * Handles puzzle move validation and game state updates
 * @returns true if move was handled, false if invalid
 */
export function handlePuzzleMove({
    move,
    gameState,
    setGameState,
    setIncorrectMoveSquare,
    setShowIncorrectMoveModal,
    setShowPuzzleCompleteModal,
    resetSelection,
    puzzleStateBeforeMove,
}: PuzzleMoveHandlerParams): boolean {
    const currentIndex = gameState.puzzleIndex ?? 0;
    const solution = gameState.puzzleSolution ?? [];

    // Puzzle already complete
    if (currentIndex >= solution.length) {
        setShowPuzzleCompleteModal(true);
        resetSelection();
        return false;
    }

    // Save state before move for potential undo
    puzzleStateBeforeMove.current = {
        fen: gameState.game?.fen() ?? "",
        index: currentIndex,
    };

    const expectedMoveSAN = solution[currentIndex];
    const clonedGame = new Chess(gameState.game?.fen());
    const appliedMove = clonedGame.move({
        from: move.from,
        to: move.to,
        promotion: "q",
    });

    if (!appliedMove) {
        resetSelection();
        return false;
    }

    // Apply move visually
    setGameState((prev: any) => ({
        ...prev,
        game: new Chess(clonedGame.fen()),
        lastMove: { from: appliedMove.from, to: appliedMove.to },
        puzzleHintSquare: null,
        puzzleHintDestination: null,
        puzzleHintLevel: 0,
    }));

    resetSelection();

    // Handle incorrect move
    if (appliedMove.san !== expectedMoveSAN) {
        setIncorrectMoveSquare(appliedMove.to);
        setTimeout(() => setShowIncorrectMoveModal(true), 400);
        return true;
    }

    // Move is correct - update puzzle index
    setGameState((prev: any) => ({
        ...prev,
        puzzleIndex: currentIndex + 1,
    }));

    // Apply opponent's response
    applyOpponentResponse(
        currentIndex + 1,
        solution,
        clonedGame,
        setGameState,
        setShowPuzzleCompleteModal,
    );

    return true;
}

/**
 * Applies the opponent's response move after a correct player move
 */
function applyOpponentResponse(
    newIndex: number,
    solution: string[],
    clonedGame: Chess,
    setGameState: React.Dispatch<React.SetStateAction<any>>,
    setShowPuzzleCompleteModal: (show: boolean) => void,
): void {
    if (newIndex >= solution.length) {
        // Puzzle complete - no opponent response needed
        setTimeout(() => setShowPuzzleCompleteModal(true), 300);
        return;
    }

    setTimeout(() => {
        const opponentSAN = solution[newIndex];
        const gameForOpponent = new Chess(clonedGame.fen());
        const opponentMove = gameForOpponent.move(opponentSAN);

        if (!opponentMove) {
            console.error("Failed to apply opponent move:", opponentSAN);
            return;
        }

        const finalIndex = newIndex + 1;

        // Check if puzzle is complete after opponent's move
        if (finalIndex >= solution.length) {
            setTimeout(() => setShowPuzzleCompleteModal(true), 300);
        }

        setGameState((prev: any) => ({
            ...prev,
            game: gameForOpponent,
            puzzleIndex: finalIndex,
            lastMove: { from: opponentMove.from, to: opponentMove.to },
        }));
    }, 500);
}
